import { useReducer, useCallback, useMemo, useEffect } from 'react'
import { getNextSuperCutoffPeriod } from '../../../utils/timeUtils'
import ability from '../../../abilities/ability'

// Permesso CASL granulare: chi NON lo ha vede solo il messaggio "contattare team AEP"
// nello step Handle errors (vedi TODO-supercutoff-db-permission-setup.md §13).
// Mappato a DB su POST /supercutoff/errors/retry; il superAdmin lo riceve via enumerazione.
const ERRORS_HANDLER_ACTION = 'supercutoff-errors:Read'
const ERRORS_HANDLER_SUBJECT = 'supercutoff-errors'

// ============================================================
// Definizione degli step del wizard
// L'ordine è sequenziale e bloccato: ogni step si sblocca
// solo dopo il completamento del precedente.
// ============================================================
export const WIZARD_STEPS = [
  { id: 'cr_block',        title: 'Block CR creation' },
  { id: 'bulk_approve',    title: 'Bulk approve hours & expenses' },
  { id: 'revenue_reset',   title: 'Reset revenues' },
  { id: 'cr_cleaner',      title: 'Clean open CRs' },
  { id: 'set_supercutoff', title: 'Set SuperCutoff PM' },
  { id: 'progress',        title: 'Monitor closure progress' },
  { id: 'errors',          title: 'Handle errors' },
  { id: 'cr_unblock',      title: 'Unblock CR creation' },
]

// ============================================================
// Action types del reducer locale
// HYDRATE è la nuova transizione che rimpiazza lo stato locale con quello
// derivato dal payload di GET /supercutoff/state (single source of truth lato BE).
// ============================================================
const WIZARD_ACTIONS = {
  HYDRATE:        'HYDRATE',
  SET_STEP_ERROR: 'SET_STEP_ERROR',
  SET_STEP_DATA:  'SET_STEP_DATA',
  SELECT_STEP:    'SELECT_STEP',
}

// ============================================================
// Costruzione dello stato iniziale (run non attiva)
// ============================================================

const buildInitialStepStatuses = () =>
  WIZARD_STEPS.reduce((acc, step, idx) => ({
    ...acc,
    [step.id]: idx === 0 ? 'in_progress' : 'pending',
  }), {})

// Funzione invece di costante: garantisce uno stato fresco a ogni mount.
// stepData ora contiene solo dati effimeri locali (lastUpdated del progress).
// Le altre chiavi vivono nello store Redux o nel wizardState idratato.
const buildInitialState = () => ({
  stepStatuses: buildInitialStepStatuses(),
  activeStepId: WIZARD_STEPS[0].id,
  stepData: {
    progress: { lastUpdated: null },
  },
})

// ============================================================
// Idratazione dal payload di GET /supercutoff/state
// ============================================================

/**
 * Verifica se il payload restituito da GET /supercutoff/state rappresenta
 * una run attiva. Quando non c'è una run il backend ritorna i campi a null
 * (vedi spec §4 e ANALISI-TECNICA §13.5).
 */
const hasActiveRun = (wizardState) => {
  if (!wizardState) return false
  if (!wizardState.execution_id) return false
  if (!wizardState.steps) return false
  if (!wizardState.steps.length) return false
  return true
}

/**
 * Indicizza l'array steps del payload BE (`[{ step_id, status, result_json, completed_at }]`)
 * in una mappa keyed by step_id, allineando la shape interna del wizard a quella usata
 * in mergedStepData. La normalizzazione è fatta una volta sola in idratazione/merge.
 */
const indexSteps = (steps) => {
  if (!steps || !steps.length) return {}
  return steps.reduce((acc, s) => {
    acc[s.step_id] = s
    return acc
  }, {})
}

/**
 * Costruisce lo stato locale del wizardReducer a partire dal payload BE.
 * Se non esiste una run attiva, restituisce lo stato pulito di partenza.
 */
const hydrateFromWizardState = (wizardState) => {
  if (!hasActiveRun(wizardState)) {
    return buildInitialState()
  }

  const stepsByKey = indexSteps(wizardState.steps)

  const stepStatuses = WIZARD_STEPS.reduce((acc, step) => {
    const beStep = stepsByKey[step.id]
    acc[step.id] = beStep ? beStep.status : 'pending'
    return acc
  }, {})

  // active_step_id dal BE è la fonte preferita; fallback al primo step se assente.
  const activeStepId = wizardState.active_step_id || WIZARD_STEPS[0].id

  // Il BE non usa lo status 'in_progress' per gli step del wizard: lo step attivo
  // resta 'pending' finché non viene completato. Lo normalizziamo lato locale a
  // 'in_progress' per allineare il modello mentale del FE — il reducer SELECT_STEP,
  // la sidebar (isStepSelectable) e la badge di stato dello step attivo si basano
  // tutti su 'in_progress'. Senza questa normalizzazione, navigando indietro a uno
  // step completato si perde la possibilità di tornare su quello attivo.
  if (stepStatuses[activeStepId] === 'pending') {
    stepStatuses[activeStepId] = 'in_progress'
  }

  return {
    stepStatuses,
    activeStepId,
    stepData: {
      progress: { lastUpdated: null },
    },
  }
}

// ============================================================
// Reducer locale — gestisce lo stato UI del wizard
// I dati di business vivono in Redux. Qui solo: stepStatuses,
// activeStepId, stepData effimero (es. progress.lastUpdated).
// ============================================================
const wizardReducer = (state, action) => {
  switch (action.type) {

    case WIZARD_ACTIONS.HYDRATE:
      return hydrateFromWizardState(action.wizardState)

    case WIZARD_ACTIONS.SET_STEP_ERROR:
      return {
        ...state,
        stepStatuses: { ...state.stepStatuses, [action.stepId]: 'error' },
      }

    case WIZARD_ACTIONS.SET_STEP_DATA:
      return {
        ...state,
        stepData: {
          ...state.stepData,
          [action.stepId]: { ...state.stepData[action.stepId], ...action.patch },
        },
      }

    case WIZARD_ACTIONS.SELECT_STEP: {
      const status = state.stepStatuses[action.stepId]
      if (status !== 'completed' && status !== 'in_progress') {
        return state
      }

      // Guard "punto di non ritorno": specchio della logica in isStepSelectable
      // (SuperCutoffWizard.utils.js). Quando `set_supercutoff` e' completed, gli
      // step fino a `set_supercutoff` incluso non sono piu' navigabili anche se
      // completati. Difesa contro chiamate dirette al reducer che bypassino la sidebar.
      const POINT_OF_NO_RETURN_STEP_ID = 'set_supercutoff'
      if (state.stepStatuses[POINT_OF_NO_RETURN_STEP_ID] === 'completed') {
        const triggerIndex = WIZARD_STEPS.findIndex(function(s) { return s.id === POINT_OF_NO_RETURN_STEP_ID })
        const targetIndex = WIZARD_STEPS.findIndex(function(s) { return s.id === action.stepId })
        if (triggerIndex !== -1 && targetIndex !== -1 && targetIndex <= triggerIndex) {
          return state
        }
      }

      return { ...state, activeStepId: action.stepId }
    }

    default:
      // Warning in development per action types non riconosciuti
      if (process.env.NODE_ENV === 'development') {
        console.warn('useSupercutoffWizard: action non riconosciuta', action.type)
      }
      return state
  }
}

/**
 * Hook di orchestrazione del wizard SuperCutoff.
 * Incapsula tutta la logica di stato UI, costruzione props e handler,
 * lasciando il container come thin wrapper.
 *
 * Lo stato del wizard è idratato dal payload di GET /supercutoff/state
 * (single source of truth lato BE) e viene rifettato dopo ogni execute
 * per riflettere le transizioni server-side (vedi Opzione A in spec §5).
 *
 * @param {Object} reduxData    Dati e loading states dallo store Redux (incluso wizardState)
 * @param {Object} dispatchers  Thunk dispatchers da mapDispatchToProps
 * @returns {Object} steps, activeStepId, activeStepTitle, stepData, handlers, loadings, onSelectStep
 */
const useSupercutoffWizard = (reduxData, dispatchers) => {
  const {
    loadingCrToggle,
    loadingBulkApprovePreview,
    loadingBulkApproveExecute,
    loadingRevenueResetPreview,
    loadingRevenueResetExecute,
    loadingOpenCrs,
    loadingCrClean,
    loadingSetSupercutoff,
    loadingProgress,
    loadingErrors,
    loadingRetryErrors,
    loadingCompleteErrors,
    bulkApprovePreview,
    bulkApproveResult,
    revenueResetPreview,
    revenueResetResult,
    openCrs,
    crCleanResult,
    supercutoffResult,
    progress,
    errors,
    retryResult,
    wizardState,
    cutoffPm,
    loadingCutoffPm,
  } = reduxData

  const {
    crToggle,
    bulkApprovePreview: dispatchBulkApprovePreview,
    bulkApproveExecute,
    revenueResetPreview: dispatchRevenueResetPreview,
    revenueResetExecute,
    getOpenCrs,
    crClean,
    setSupercutoff,
    getProgress,
    getErrors,
    retryErrors,
    completeErrors,
    getSupercutoffState,
  } = dispatchers

  // L'init parte dallo stato pulito e viene poi idratato via useEffect quando
  // wizardState arriva da Redux. Questo evita re-init non controllati e mantiene
  // il flusso "Redux → reducer locale" sempre nella stessa direzione.
  const [wizardLocalState, dispatch] = useReducer(wizardReducer, undefined, buildInitialState)
  const { stepStatuses, activeStepId, stepData } = wizardLocalState

  // Ridratazione del reducer locale al cambio di wizardState (mount, refresh post-execute,
  // retry dopo errore). buildInitialState resta il fallback per il caso "no run attiva".
  useEffect(() => {
    dispatch({ type: WIZARD_ACTIONS.HYDRATE, wizardState })
  }, [wizardState])

  const setStepError = useCallback((stepId) => {
    dispatch({ type: WIZARD_ACTIONS.SET_STEP_ERROR, stepId })
  }, [])

  const setStepData = useCallback((stepId, patch) => {
    dispatch({ type: WIZARD_ACTIONS.SET_STEP_DATA, stepId, patch })
  }, [])

  // Fetch del nuovo state dopo ogni execute riuscito (Opzione A — spec §5).
  // L'idratazione via useEffect riallineerà il reducer locale.
  const refreshState = useCallback(() => {
    getSupercutoffState()
  }, [getSupercutoffState])

  // ── Step 1 — Block CR ──────────────────────────────────
  const handleCrBlock = useCallback(() => {
    crToggle(
      false,
      () => refreshState(),
      () => setStepError('cr_block'),
    )
  }, [crToggle, refreshState, setStepError])

  // ── Step 2 — Bulk Approve Preview ─────────────────────
  const handleBulkApprovePreview = useCallback((params) => {
    // Guard contro doppio invio durante il loading
    if (loadingBulkApprovePreview) return
    dispatchBulkApprovePreview(params)
  }, [dispatchBulkApprovePreview, loadingBulkApprovePreview])

  // ── Step 3 — Bulk Approve Execute ─────────────────────
  const handleBulkApproveExecute = useCallback((params) => {
    bulkApproveExecute(
      params,
      () => refreshState(),
      () => setStepError('bulk_approve'),
    )
  }, [bulkApproveExecute, refreshState, setStepError])

  // ── Step 4 — Revenue Reset Preview ────────────────────
  const handleRevenueResetPreview = useCallback((params) => {
    // Guard contro doppio invio durante il loading
    if (loadingRevenueResetPreview) return
    dispatchRevenueResetPreview(params)
  }, [dispatchRevenueResetPreview, loadingRevenueResetPreview])

  // ── Step 5 — Revenue Reset Execute ────────────────────
  const handleRevenueResetExecute = useCallback((params) => {
    revenueResetExecute(
      params,
      () => refreshState(),
      () => setStepError('revenue_reset'),
    )
  }, [revenueResetExecute, refreshState, setStepError])

  // ── Step 6a — Load Open CRs ───────────────────────────
  // Nota: questo è un GET puro. Il BE non transitiona lo step `cr_cleaner`
  // su questo endpoint (lo fa solo /cr-clean). Se la lista è vuota, si
  // affida al pulsante di skip per chiamare /cr-clean con pfe_ids=[].
  const handleLoadOpenCrs = useCallback(() => {
    getOpenCrs(
      undefined,
      () => setStepError('cr_cleaner'),
    )
  }, [getOpenCrs, setStepError])

  // ── Step 6b — CR Clean ────────────────────────────────
  const handleCrClean = useCallback((params) => {
    crClean(
      params,
      () => refreshState(),
      () => setStepError('cr_cleaner'),
    )
  }, [crClean, refreshState, setStepError])

  // ── Step 6c — CR Clean Skip ───────────────────────────
  // Lo skip è una chiamata cr-clean con pfe_ids vuoto: garantisce che il BE
  // marchi lo step come completed e avanzi current_step.
  const handleCrCleanSkip = useCallback(() => {
    crClean(
      { pfe_ids: [] },
      () => refreshState(),
      () => setStepError('cr_cleaner'),
    )
  }, [crClean, refreshState, setStepError])

  // ── Step 7 — Set SuperCutoff ──────────────────────────
  const handleSetSuperCutoff = useCallback((params) => {
    setSupercutoff(
      params,
      () => refreshState(),
      () => setStepError('set_supercutoff'),
    )
  }, [setSupercutoff, refreshState, setStepError])

  // ── Step 8 — Refresh Progress ─────────────────────────
  // Il BE transitiona automaticamente lo step `progress` → `completed` quando
  // il job è in stato terminale (vedi spec §13.6). Dopo ogni refresh la UI
  // ricarica anche lo state per intercettare la transizione server-side.
  const handleRefreshProgress = useCallback(() => {
    getProgress(
      (data) => {
        if (!data) return
        setStepData('progress', { lastUpdated: new Date().toLocaleString('it-IT') })
        const isTerminal = data.status === 'completed' || data.status === 'completed_with_errors'
        if (isTerminal) {
          refreshState()
        } else if (data.status === 'error') {
          setStepError('progress')
        }
      },
      () => setStepError('progress'),
    )
  }, [getProgress, setStepData, refreshState, setStepError])

  // ── Step 9a — Load Errors ─────────────────────────────
  const handleLoadErrors = useCallback(() => {
    getErrors(
      undefined,
      () => setStepError('errors'),
    )
  }, [getErrors, setStepError])

  // ── Step 9b — Retry Errors ────────────────────────────
  const handleRetryErrors = useCallback((params) => {
    retryErrors(
      params,
      undefined,
      () => setStepError('errors'),
    )
  }, [retryErrors, setStepError])

  // ── Step 9c — Errors Complete ─────────────────────────
  // Sostituito l'advance locale con la chiamata al nuovo endpoint
  // POST /supercutoff/errors/complete (spec §7).
  const handleErrorsComplete = useCallback(() => {
    completeErrors(() => refreshState())
  }, [completeErrors, refreshState])

  // ── Step 10 — Unblock CR ──────────────────────────────
  const handleCrUnblock = useCallback(() => {
    crToggle(
      true,
      () => refreshState(),
      () => setStepError('cr_unblock'),
    )
  }, [crToggle, refreshState, setStepError])

  // ── Navigazione sidebar ───────────────────────────────
  const handleSelectStep = useCallback((stepId) => {
    dispatch({ type: WIZARD_ACTIONS.SELECT_STEP, stepId })
  }, [])

  // ── Costruzione props stabilizzate con useMemo ────────

  const steps = useMemo(
    () => WIZARD_STEPS.map(s => ({ ...s, status: stepStatuses[s.id] })),
    [stepStatuses],
  )

  // crAvailable arriva da /state. Fallback a false finché wizardState è null
  // (durante il caricamento iniziale): la UI mostra il loader, non un valore fittizio.
  const crAvailable = wizardState ? wizardState.cr_available : false

  // Indicizzazione degli step BE per accedere a result_json di cr_block / cr_unblock.
  // useRef per stabilizzare il riferimento tra render quando wizardState non cambia.
  const beStepsByKey = useMemo(
    () => indexSteps(wizardState ? wizardState.steps : null),
    [wizardState],
  )

  // ProgressLastUpdated è dato effimero locale, non viene da BE.
  const progressLastUpdated = stepData.progress ? stepData.progress.lastUpdated : null

  // Permesso AEP per gestire dettaglio + retry degli errori. Calcolato qui per essere
  // passato come prop a StepErrors (componente presentazionale puro). Si rivaluta a ogni
  // render: ability.can e' sincrono e leggero, non serve memoizzare.
  const canHandleErrors = ability.can(ERRORS_HANDLER_ACTION, ERRORS_HANDLER_SUBJECT)

  // Calcolo del "mese successivo" vincolato per gli step Bulk approve e Set SuperCutoff PM.
  // La fonte di verita e cutoff_pm: il record con to_date massima determina il mese successivo
  // selezionabile. Se la lista e vuota o la chiamata e fallita, nextPeriod e null e gli step
  // mostreranno lo stato di errore bloccante.
  const nextPeriod = useMemo(
    () => getNextSuperCutoffPeriod(cutoffPm),
    [cutoffPm],
  )

  // Merge tra dati Redux (effimeri di sessione) e dati persistiti (result_json
  // dei toggle CR). Il refresh dello state post-execute riallinea automaticamente
  // entrambe le sorgenti, quindi non serve più stato locale per cr_block/cr_unblock.
  const mergedStepData = useMemo(
    () => {
      const crBlockBe = beStepsByKey.cr_block
      const crUnblockBe = beStepsByKey.cr_unblock
      return {
        cr_block: {
          crAvailable,
          result: crBlockBe && crBlockBe.result_json ? crBlockBe.result_json : null,
        },
        bulk_approve:    { preview: bulkApprovePreview, result: bulkApproveResult, nextPeriod: nextPeriod },
        revenue_reset:   { preview: revenueResetPreview, result: revenueResetResult, nextPeriod: nextPeriod },
        cr_cleaner:      { openCrs, result: crCleanResult },
        set_supercutoff: { result: supercutoffResult, nextPeriod: nextPeriod },
        progress:        { progress, lastUpdated: progressLastUpdated },
        errors:          { errors, retryResult, canHandleErrors },
        cr_unblock: {
          crAvailable,
          result: crUnblockBe && crUnblockBe.result_json ? crUnblockBe.result_json : null,
        },
      }
    },
    [
      beStepsByKey,
      crAvailable,
      progressLastUpdated,
      nextPeriod,
      canHandleErrors,
      bulkApprovePreview, bulkApproveResult,
      revenueResetPreview, revenueResetResult,
      openCrs, crCleanResult,
      supercutoffResult,
      progress,
      errors,
      retryResult,
    ],
  )

  const loadings = useMemo(
    () => ({
      crToggle:            loadingCrToggle,
      bulkApprovePreview:  loadingBulkApprovePreview,
      bulkApproveExecute:  loadingBulkApproveExecute,
      revenueResetPreview: loadingRevenueResetPreview,
      revenueResetExecute: loadingRevenueResetExecute,
      crOpen:              loadingOpenCrs,
      crClean:             loadingCrClean,
      setSuperCutoff:      loadingSetSupercutoff,
      progress:            loadingProgress,
      errorsGet:           loadingErrors,
      errorsRetry:         loadingRetryErrors,
      errorsComplete:      loadingCompleteErrors,
      // crStatus è il flag legacy del loader sul toggle CR. Ora cr_available
      // arriva da /state e il container intercetta già la fase pre-hydration
      // mostrando il loader globale. Quando il wizard si renderizza, wizardState
      // è sempre non-null, quindi questo flag è sempre false.
      crStatus:            false,
      // Loading del fetch /pm/cutoffs/, usato dagli step Bulk approve e Set SuperCutoff PM
      // per mostrare un loader sulla selezione del periodo finche cutoff_pm non e disponibile.
      cutoffPm:            loadingCutoffPm,
    }),
    [
      loadingCrToggle,
      loadingBulkApprovePreview, loadingBulkApproveExecute,
      loadingRevenueResetPreview, loadingRevenueResetExecute,
      loadingOpenCrs, loadingCrClean,
      loadingSetSupercutoff,
      loadingProgress,
      loadingErrors, loadingRetryErrors,
      loadingCompleteErrors,
      loadingCutoffPm,
    ],
  )

  const handlers = useMemo(
    () => ({
      cr_block:        { onExecute: handleCrBlock },
      bulk_approve:    { onPreview: handleBulkApprovePreview, onExecute: handleBulkApproveExecute },
      revenue_reset:   { onPreview: handleRevenueResetPreview, onExecute: handleRevenueResetExecute },
      cr_cleaner:      { onLoadCrs: handleLoadOpenCrs, onExecute: handleCrClean, onSkip: handleCrCleanSkip },
      set_supercutoff: { onExecute: handleSetSuperCutoff },
      progress:        { onRefresh: handleRefreshProgress },
      errors:          { onLoadErrors: handleLoadErrors, onRetry: handleRetryErrors, onComplete: handleErrorsComplete },
      cr_unblock:      { onExecute: handleCrUnblock },
    }),
    [
      handleCrBlock,
      handleBulkApprovePreview, handleBulkApproveExecute,
      handleRevenueResetPreview, handleRevenueResetExecute,
      handleLoadOpenCrs, handleCrClean, handleCrCleanSkip,
      handleSetSuperCutoff,
      handleRefreshProgress,
      handleLoadErrors, handleRetryErrors, handleErrorsComplete,
      handleCrUnblock,
    ],
  )

  // Titolo dello step attivo — usato per l'annuncio aria-live nel container
  const activeStepTitle = useMemo(
    () => {
      const step = WIZARD_STEPS.find(s => s.id === activeStepId)
      return step ? step.title : ''
    },
    [activeStepId],
  )

  return {
    steps,
    activeStepId,
    activeStepTitle,
    stepData: mergedStepData,
    handlers,
    loadings,
    onSelectStep: handleSelectStep,
  }
}

export default useSupercutoffWizard
